import * as React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Retracked from 'js/app/retracked';

import { Box, Button, GradientWrapper, color, gradient } from '@coursera/coursera-ui';

import CourseraLogo from 'bundles/page/components/CourseraLogo';

import _t from 'i18n!nls/degree-home';

import 'css!./__styl__/IntroModal';

const MOCK_SRC = 'https://s3.amazonaws.com/coursera_assets/degree-home/degree_home_mockup_large.svg';
const MOCK_MOBILE_SRC = 'https://s3.amazonaws.com/coursera_assets/degree-home/degree_home_mockup_small.svg';
const LEFT_BG_PATTERN = 'https://s3.amazonaws.com/coursera_assets/degree-home/left_pattern.png';
const RIGHT_BG_PATTERN = 'https://s3.amazonaws.com/coursera_assets/degree-home/right_pattern.png';

const GradientBox = (props: {}) => <Box flexDirection="column" alignItems="center" justifyContent="start" {...props} />;

type Props = {
  modalName: string;
  handleClose?: (s?: string) => void;
  className?: string;
  trackingName: string;
  'data-js'?: string;
  'data-e2e'?: string;
  data?: any;
};

class IntroModal extends React.Component<Props> {
  modalContent: HTMLElement | null = null;

  static contextTypes = {
    _eventData: PropTypes.object,
  };

  static defaultProps = {
    'data-js': 'IntroModal',
  };

  componentDidMount() {
    const { data, trackingName } = this.props;
    const { _eventData } = this.context;
    window.addEventListener('keydown', this.checkForEsc, true);
    window.addEventListener('keydown', this.resetFocus, true);
    if (document.body) {
      document.body.classList.add('c-phoenix-modal-open');
    }
    if (this.modalContent) {
      this.modalContent.setAttribute('tabindex', '0');
      this.modalContent.focus();
    }
    Retracked.trackComponent(_eventData, data, trackingName, 'show');
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.checkForEsc, true);
    window.removeEventListener('keydown', this.resetFocus, true);
    if (document.body) {
      document.body.classList.remove('c-phoenix-modal-open');
    }
  }

  handleXClose = (e: Event) => {
    const { data, trackingName, handleClose } = this.props;
    const { _eventData } = this.context;
    e.preventDefault();

    if (handleClose) {
      handleClose('x');
    }

    Retracked.trackComponent(_eventData, data, trackingName, 'close');
  };

  handleClose = () => {
    const { data, trackingName, handleClose } = this.props;
    const { _eventData } = this.context;

    if (handleClose) {
      handleClose();
      Retracked.trackComponent(_eventData, data, trackingName, 'close');
    }
  };

  checkForEsc = (e: KeyboardEvent) => {
    if (e.keyCode === 27 || e.key === 'Escape') {
      this.handleClose();
    }
  };

  resetFocus = (e: Event) => {
    if (this.modalContent && !this.modalContent.contains(e.target as Node)) {
      e.stopPropagation();
      this.modalContent.focus();
    }
  };

  render() {
    const { _eventData } = this.context;
    const namespace = (_eventData && _eventData.namespace) || {};
    const { app, page } = namespace;
    const { trackingName: component, className, modalName } = this.props;

    // in SSR we dismiss the modal by removing the query param that shows it
    const hrefToRemoveQueryParams = '?';

    const xButton = (
      <div className="x-button m-b-2 fadeInUp delay3">
        <Button
          isThemeDark={true}
          type="primary"
          onClick={this.handleXClose}
          aria-label={_t('close')}
          htmlAttributes={{
            href: hrefToRemoveQueryParams,
            'data-track': true,
            'data-track-app': app,
            'data-track-page': page,
            'data-track-action': 'close',
            'data-track-component': component,
            'data-e2e': 'close-modal-button',
          }}
          customStyles={{
            Button: {
              ':focus': {
                outlineColor: color.white,
              },
            },
          }}
        >
          {_t('Check it out')}
        </Button>
      </div>
    );

    const classes = classNames('rc-IntroModal', className);

    return (
      <div
        className={classes}
        data-js={this.props['data-js']}
        data-track={true}
        data-track-app={app}
        data-track-page={page}
        data-track-action="show"
        data-track-component={component}
        data-e2e={this.props['data-e2e']}
      >
        <div className="c-modal-overlay" />
        <div
          className="c-modal-content"
          ref={(c) => {
            this.modalContent = c;
          }}
          role="dialog"
          aria-label={modalName}
        >
          <GradientWrapper
            gradient={gradient.coursera}
            rootClassName="text-primary-dark gradient-wrapper"
            tag={GradientBox}
          >
            <CourseraLogo className="coursera-logo m-t-2 m-l-2" hexColorCode={color.white} />
            <div className="vertical-box align-items-vertical-center content-container">
              <img className="mock-dashboard bt3-hidden-xs fadeInUp" src={MOCK_SRC} alt={_t('Mock dashboard')} />
              <img
                className="mock-dashboard bt3-visible-xs fadeInUp"
                src={MOCK_MOBILE_SRC}
                alt={_t('Mock dashboard')}
              />
              <span className="header-message fadeInUp delay1">{_t('Welcome to your home page')}</span>
              <span className="body-message m-r-2 m-b-2 m-l-2 m-t-1 fadeInUp delay2">
                {_t(
                  'You can use it to see what you’re taking, when your assignments are due, and the progress you’re making every step of the way.'
                )}
              </span>
              {xButton}
              <img
                className="bg-pattern left bt3-visible-lg"
                src={LEFT_BG_PATTERN}
                alt={_t('Left background pattern')}
              />
              <img
                className="bg-pattern right bt3-visible-lg"
                src={RIGHT_BG_PATTERN}
                alt={_t('Right background pattern')}
              />
            </div>
          </GradientWrapper>
        </div>
      </div>
    );
  }
}

export default IntroModal;
